import React, { useEffect, useRef, useState } from 'react';
import { useUISettings } from '../context/UISettingsContext';

const FluidBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resolvedTheme } = useUISettings();
  const [isMobileBackground, setIsMobileBackground] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px), (hover: none), (pointer: coarse)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 768px), (hover: none), (pointer: coarse)');
    const update = () => setIsMobileBackground(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (isMobileBackground) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) return;

    let animationFrameId: number;
    let lastTime = 0;
    const FPS_LIMIT = 1000 / 35;
    const mouse = { x: -1000, y: -1000, targetX: -1000, targetY: -1000 };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.targetX = e.clientX;
      mouse.targetY = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    const resize = () => {
      const scale = 0.4;
      canvas.width = window.innerWidth * scale;
      canvas.height = window.innerHeight * scale;
    };
    window.addEventListener('resize', resize);
    resize();

    const orbs =
      resolvedTheme === 'dark'
        ? [
            { x: 0.2, y: 0.3, r: 0.6, color: 'rgba(99, 102, 241, 0.2)', vx: 0, vy: 0 },
            { x: 0.8, y: 0.7, r: 0.5, color: 'rgba(56, 189, 248, 0.15)', vx: 0, vy: 0 },
            { x: 0.5, y: 0.5, r: 0.4, color: 'rgba(129, 140, 248, 0.18)', vx: 0, vy: 0 },
            { x: 0.1, y: 0.8, r: 0.5, color: 'rgba(15, 118, 110, 0.14)', vx: 0, vy: 0 },
            { x: 0.9, y: 0.1, r: 0.4, color: 'rgba(30, 41, 59, 0.5)', vx: 0, vy: 0 },
          ]
        : [
            { x: 0.2, y: 0.3, r: 0.6, color: 'rgba(99, 102, 241, 0.15)', vx: 0, vy: 0 },
            { x: 0.8, y: 0.7, r: 0.5, color: 'rgba(139, 92, 246, 0.15)', vx: 0, vy: 0 },
            { x: 0.5, y: 0.5, r: 0.4, color: 'rgba(165, 180, 252, 0.2)', vx: 0, vy: 0 },
            { x: 0.1, y: 0.8, r: 0.5, color: 'rgba(196, 181, 253, 0.1)', vx: 0, vy: 0 },
            { x: 0.9, y: 0.1, r: 0.4, color: 'rgba(224, 231, 255, 0.4)', vx: 0, vy: 0 },
          ];

    let time = 0;

    const animate = (currentTime: number) => {
      animationFrameId = requestAnimationFrame(animate);

      const deltaTime = currentTime - lastTime;
      if (deltaTime < FPS_LIMIT) return;
      lastTime = currentTime;

      time += 0.002;

      mouse.x += (mouse.targetX - mouse.x) * 0.08;
      mouse.y += (mouse.targetY - mouse.y) * 0.08;

      const scaleX = canvas.width / window.innerWidth;
      const scaleY = canvas.height / window.innerHeight;
      const mX = mouse.x * scaleX;
      const mY = mouse.y * scaleY;

      ctx.fillStyle = resolvedTheme === 'dark' ? '#07111f' : '#f8fafc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      orbs.forEach((orb, i) => {
        const ambientX = Math.sin(time + i) * 0.12 * canvas.width;
        const ambientY = Math.cos(time * 0.8 + i) * 0.12 * canvas.height;

        const baseX = orb.x * canvas.width + ambientX;
        const baseY = orb.y * canvas.height + ambientY;

        const dx = mX - baseX;
        const dy = mY - baseY;
        const distSq = dx * dx + dy * dy;
        const interactionRadius = canvas.width * 0.5;
        const interactionRadiusSq = interactionRadius * interactionRadius;

        let interactX = 0;
        let interactY = 0;

        if (distSq < interactionRadiusSq) {
          const dist = Math.sqrt(distSq);
          const force = (interactionRadius - dist) / interactionRadius;
          const angle = Math.atan2(dy, dx);
          const pushStrength = 80 * scaleX;
          interactX = -Math.cos(angle) * force * pushStrength;
          interactY = -Math.sin(angle) * force * pushStrength;
        }

        orb.vx += (interactX - orb.vx) * 0.05;
        orb.vy += (interactY - orb.vy) * 0.05;

        const x = baseX + orb.vx;
        const y = baseY + orb.vy;
        const radius = orb.r * canvas.width;

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, orb.color);
        gradient.addColorStop(1, resolvedTheme === 'dark' ? 'rgba(7, 17, 31, 0)' : 'rgba(248, 250, 252, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [resolvedTheme, isMobileBackground]);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" style={{ backgroundColor: 'var(--app-bg)' }}>
      {!isMobileBackground && (
        <>
          <canvas
            ref={canvasRef}
            className="h-[110%] w-[110%] -translate-x-[5%] -translate-y-[5%]"
            style={{
              filter: 'blur(80px)',
              willChange: 'transform',
            }}
          />

          <div
            className="absolute inset-0 pointer-events-none opacity-[0.02]"
            style={{
              backgroundImage:
                `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              mixBlendMode: resolvedTheme === 'dark' ? 'soft-light' : 'overlay',
            }}
          />
        </>
      )}

      {isMobileBackground && (
        <div
          className="absolute inset-0"
          style={{
            background:
              resolvedTheme === 'dark'
                ? 'radial-gradient(120% 80% at 18% 22%, rgba(99,102,241,0.22), transparent 58%), radial-gradient(90% 70% at 82% 70%, rgba(56,189,248,0.14), transparent 62%), linear-gradient(180deg, #07111f 0%, #0b172a 100%)'
                : 'radial-gradient(110% 75% at 20% 24%, rgba(99,102,241,0.18), transparent 58%), radial-gradient(90% 65% at 80% 70%, rgba(139,92,246,0.14), transparent 62%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
          }}
        />
      )}
    </div>
  );
};

export default FluidBackground;
